/**
 * The only file that reads a rule's `conditions` / `actions`. The API types both as bare
 * `object[]` (backend #288), so everything here is validated with guards, and any stored
 * rule this builder cannot round-trip is reported read-only rather than coerced.
 */
import type { components } from '../../api/generated/schema'
import { formatCents, parseMoney, parseMoneyInput } from '../../api/parseMoney'

export type FieldKey =
  | 'merchant_name' | 'merchant_name_raw' | 'amount' | 'note' | 'account_id' | 'account_type' | 'date'
  | 'day_of_week'
export type OpKey =
  | 'equals' | 'contains' | 'starts_with' | 'ends_with' | 'is_in' | 'before' | 'after' | 'between'
  | 'greater_than' | 'less_than' | 'greater_or_equal' | 'not_equals' | 'not_contains' | 'not_in'
/** Which way the money moved — an amount condition only (backend #442). `spent`
 *  and `received` compare the size of an outflow or an inflow; `any` the size of
 *  either. A stored amount with no direction keeps the backend's signed meaning,
 *  which this builder cannot express, so such a rule is read-only. */
export type Direction = 'spent' | 'received' | 'any'
export type ActionKey =
  | 'set_category' | 'add_tag' | 'remove_tag' | 'set_merchant' | 'set_note' | 'append_note'
  | 'flag_for_review' | 'exclude_from_budget' | 'exclude_from_reports'

export type ConditionDraft = {
  field: FieldKey
  op: OpKey
  value: string
  value2: string
  list: string[]
  /** An amount condition's direction; absent on every other field. Optional
   *  rather than a field-keyed union so the reducer's spreads stay simple —
   *  `writeCondition` is what keeps it off the wire for a non-amount field. */
  direction?: Direction
}
export type GroupDraft = { join: 'and' | 'or'; conditions: ConditionDraft[] }
export type ActionDraft = { type: ActionKey; value: string }
export type RuleDraft = {
  id: string | null
  name: string
  enabled: boolean
  groups: GroupDraft[]
  actions: ActionDraft[]
  applyToPast: boolean
  /** ISO date, or null (= 90 days). */
  backfillStart: string | null
}

const TEXT_OPS = [
  'equals', 'contains', 'starts_with', 'ends_with', 'is_in', 'not_equals', 'not_contains', 'not_in',
] as const

/** "Any amount" on the wire: `greater_or_equal "0"` with a direction. The
 *  builder offers no "at least", so any other `greater_or_equal` is read-only. */
export const ANY_AMOUNT = 'greater_or_equal' satisfies OpKey

export const FIELDS: Record<
  FieldKey,
  { label: string; kind: 'text' | 'select' | 'date' | 'dow' | 'money'; ops: readonly OpKey[] }
> = {
  merchant_name: { label: 'Merchant name', kind: 'text', ops: TEXT_OPS },
  merchant_name_raw: { label: 'Raw description', kind: 'text', ops: TEXT_OPS },
  amount: { label: 'Amount', kind: 'money', ops: [ANY_AMOUNT, 'equals', 'greater_than', 'less_than', 'between'] },
  account_id: { label: 'Account', kind: 'select', ops: ['equals', 'is_in', 'not_equals', 'not_in'] },
  account_type: { label: 'Account type', kind: 'select', ops: ['equals', 'is_in', 'not_equals', 'not_in'] },
  date: { label: 'Date', kind: 'date', ops: ['before', 'after', 'between'] },
  day_of_week: { label: 'Day of week', kind: 'dow', ops: ['is_in', 'equals', 'not_equals', 'not_in'] },
  note: { label: 'Note', kind: 'text', ops: TEXT_OPS },
}
export const FIELD_ORDER: readonly FieldKey[] = [
  'merchant_name', 'merchant_name_raw', 'amount', 'account_id', 'account_type', 'date', 'day_of_week', 'note',
]

/** The ops whose value is a list (comma-joined on the wire). */
export const isListOp = (op: OpKey): boolean => op === 'is_in' || op === 'not_in'

const DIRECTIONS: readonly Direction[] = ['spent', 'received', 'any']
const isDirection = (v: unknown): v is Direction => DIRECTIONS.some((d) => d === v)

/** The backend refuses an amount of 1e13 or more (backend #511): cents, exclusive. */
const AMOUNT_LIMIT_CENTS = 1e15

/**
 * An amount the person TYPED, as non-negative integer cents — or null when it
 * is not one. `parseMoneyInput` decides the format (one leading `$`, thousands
 * commas, at most two decimal places: the backend's own 2-place rule), so
 * "$1,000.50" is 100050 and "15.999" is refused. Any minus sign is refused
 * outright: the direction says which way the money moved, never a sign. So is
 * anything at or past the backend's ceiling, which would only 422 on save.
 *
 * For typed text only. A STORED value is read by `wireCents`, which is strict.
 */
export function typedCents(text: string): number | null {
  if (/[-\u2212]/.test(text)) return null
  try {
    const cents = parseMoneyInput(text)
    return cents < 0 || cents >= AMOUNT_LIMIT_CENTS ? null : cents
  } catch {
    return null
  }
}

/**
 * A STORED amount, as cents — or null. The wire's form only: a plain
 * non-negative decimal of at most two places, below the backend's ceiling. No
 * "$", no grouping commas, no padding; those are typed-input conveniences, and
 * a stored value that carries one was not written by this builder.
 */
function wireCents(value: string): number | null {
  // At most 13 integer digits: the ceiling is below 1e13 units, and a longer run
  // would make parseMoney throw rather than be refused here.
  if (!/^\d{1,13}(\.\d{1,2})?$/.test(value)) return null
  const cents = parseMoney(value)
  return cents >= AMOUNT_LIMIT_CENTS ? null : cents
}

/** Cents as the wire's plain decimal: "50" for whole units, "1000.50" otherwise. */
const wireAmount = (cents: number): string => formatCents(cents).replace(/\.00$/, '')

export const ACTIONS: Record<ActionKey, { label: string; value: 'category' | 'merchant' | 'text' | 'none' }> = {
  set_category: { label: 'Set category', value: 'category' },
  add_tag: { label: 'Add tag', value: 'text' },
  remove_tag: { label: 'Remove tag', value: 'text' },
  /** A merchant ID, chosen in the merchant picker: the backend rejects any value that is not one. */
  set_merchant: { label: 'Set merchant', value: 'merchant' },
  set_note: { label: 'Set note', value: 'text' },
  append_note: { label: 'Append note', value: 'text' },
  flag_for_review: { label: 'Flag for review', value: 'none' },
  exclude_from_budget: { label: 'Exclude from budget', value: 'none' },
  exclude_from_reports: { label: 'Exclude from reports', value: 'none' },
}
export const ACTION_ORDER: readonly ActionKey[] = [
  'set_category', 'add_tag', 'remove_tag', 'set_merchant', 'set_note', 'append_note',
  'flag_for_review', 'exclude_from_budget', 'exclude_from_reports',
]
export const MAX_ACTIONS = 5
const NAME_MAX = 100

export type Rule = components['schemas']['RuleResponse']
export type ReadRule =
  | { rule: Rule; editable: true; draft: RuleDraft }
  | {
      rule: Rule
      editable: false
      reason:
        | 'unsupported_field'
        | 'unsupported_operator'
        | 'unsupported_action'
        | 'malformed'
        /** An amount condition saved with no direction (iOS, or before #442). It
         *  compares the signed amount, so re-saving it with a direction would
         *  silently change what it matches. */
        | 'legacy_amount'
    }
type Reason = Extract<ReadRule, { editable: false }>['reason']

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)
const isField = (v: string): v is FieldKey => (FIELD_ORDER as readonly string[]).includes(v)
const isAction = (v: string): v is ActionKey => (ACTION_ORDER as readonly string[]).includes(v)

const splitList = (v: string): string[] =>
  v.split(',').map((s) => s.trim()).filter((s) => s !== '')

class Unreadable extends Error {
  reason: Reason
  constructor(reason: Reason) {
    super(reason)
    this.reason = reason
  }
}

/** A stored amount must be the wire's plain decimal (`wireCents`): a
 *  non-negative decimal of at most two places. Anything else is not coerced —
 *  the rule is read-only. */
function storedAmount(v: string): number {
  const cents = wireCents(v)
  if (cents === null) throw new Unreadable('malformed')
  return cents
}

/** `readCondition` has already sent a direction-less amount to `legacy_amount`. */
function readAmount(op: OpKey, value: string, second: string | null, direction: unknown): ConditionDraft {
  if (!isDirection(direction)) throw new Unreadable('malformed')
  if (op === ANY_AMOUNT) {
    if (storedAmount(value) !== 0) throw new Unreadable('unsupported_operator')
    return { field: 'amount', op, value: '', value2: '', list: [], direction }
  }
  storedAmount(value)
  if (op === 'between') {
    if (second == null) throw new Unreadable('malformed')
    storedAmount(second)
    return { field: 'amount', op, value, value2: second, list: [], direction }
  }
  return { field: 'amount', op, value, value2: '', list: [], direction }
}

function readCondition(raw: unknown): ConditionDraft {
  if (!isRecord(raw)) throw new Unreadable('malformed')
  const { field, operator, value, second_value: second, case_sensitive: cs, direction } = raw
  if (typeof field !== 'string' || typeof operator !== 'string' || typeof value !== 'string') {
    throw new Unreadable('malformed')
  }
  if (second != null && typeof second !== 'string') throw new Unreadable('malformed')
  if (!isField(field)) throw new Unreadable('unsupported_field')
  // Legacy before the operator lookup: whatever its operator, a signed amount
  // rule stays as it is. This is the only check for it.
  if (field === 'amount' && direction == null) throw new Unreadable('legacy_amount')
  // The backend refuses a direction on any other field; one here is corrupt.
  if (field !== 'amount' && direction != null) throw new Unreadable('malformed')
  const op = FIELDS[field].ops.find((o) => o === operator)
  if (op === undefined || cs === true) throw new Unreadable('unsupported_operator')
  if (field === 'amount') return readAmount(op, value, second ?? null, direction)
  if (isListOp(op)) return { field, op, value: '', value2: '', list: splitList(value) }
  return { field, op, value, value2: second ?? '', list: [] }
}

function readGroup(raw: unknown): GroupDraft {
  if (!isRecord(raw)) throw new Unreadable('malformed')
  const { logic_operator: join, conditions } = raw
  if ((join !== 'and' && join !== 'or') || !Array.isArray(conditions)) throw new Unreadable('malformed')
  if (conditions.length === 0) throw new Unreadable('malformed')
  return { join, conditions: conditions.map(readCondition) }
}

function readAction(raw: unknown): ActionDraft {
  if (!isRecord(raw)) throw new Unreadable('malformed')
  const { type, value } = raw
  if (typeof type !== 'string' || (value != null && typeof value !== 'string')) {
    throw new Unreadable('malformed')
  }
  if (!isAction(type)) throw new Unreadable('unsupported_action')
  return { type, value: ACTIONS[type].value === 'none' ? '' : (value ?? '') }
}

export function fromResponse(rule: Rule): ReadRule {
  try {
    if (rule.conditions.length === 0 || rule.actions.length === 0) throw new Unreadable('malformed')
    const groups = rule.conditions.map(readGroup)
    const actions = rule.actions.map(readAction)
    return {
      rule,
      editable: true,
      draft: {
        id: rule.id,
        name: rule.name,
        enabled: rule.is_enabled,
        groups,
        actions,
        applyToPast: rule.apply_to_past,
        backfillStart: rule.backfill_start_date ?? null,
      },
    }
  } catch (e) {
    if (e instanceof Unreadable) return { rule, editable: false, reason: e.reason }
    throw e
  }
}

/**
 * Why a condition the person has typed into cannot be saved — shown inline by
 * the row, and it blocks the save. A wholly blank value is not a problem (the
 * row is unfinished, and an unfinished row is left out as before); a typed one
 * that is wrong or half-done must not be quietly dropped, or "Netflix AND
 * exactly 15.999" — or "Netflix AND between 60 and [blank]" — would save as
 * "Netflix" alone.
 */
export type ConditionProblem = 'amount_format' | 'amount_incomplete' | 'amount_order' | 'amount_missing'

export function conditionProblem(c: ConditionDraft): ConditionProblem | null {
  if (c.field !== 'amount' || c.op === ANY_AMOUNT) return null
  const boxes = c.op === 'between' ? [c.value, c.value2] : [c.value]
  const typed = boxes.filter((v) => v.trim() !== '')
  const cents = typed.map(typedCents)
  if (cents.some((x) => x === null)) return 'amount_format'
  if (typed.length === 0) return 'amount_missing'
  if (typed.length < boxes.length) return 'amount_incomplete'
  const [low, high] = cents
  if (c.op === 'between' && low != null && high != null && low > high) return 'amount_order'
  return null
}

function isCompleteCondition(c: ConditionDraft): boolean {
  if (c.field === 'amount') {
    if (c.op === ANY_AMOUNT) return true
    if (conditionProblem(c) !== null) return false
  }
  if (isListOp(c.op)) return c.list.some((s) => s.trim() !== '')
  if (c.op === 'between') return c.value.trim() !== '' && c.value2.trim() !== ''
  return c.value.trim() !== ''
}
const isCompleteAction = (a: ActionDraft): boolean =>
  ACTIONS[a.type].value === 'none' || a.value.trim() !== ''

type Create = components['schemas']['RuleCreate']

type ConditionWrite = components['schemas']['ConditionItemCreate']

/**
 * An amount condition's direction. Every amount draft carries one —
 * `blankCondition` and `readAmount` both set it — so one without is a defect,
 * and it throws rather than quietly reading, showing and saving as money out.
 * The one owner of that invariant: the write, the sentence and the row's
 * control all ask here.
 */
export function amountDirection(c: ConditionDraft): Direction {
  if (c.direction === undefined) throw new Error(`ruleWire: an amount condition with no direction (${c.op})`)
  return c.direction
}

/** An amount the draft holds, as the wire's plain decimal. Only complete
 *  conditions are written, so the value has already passed `typedCents`; one
 *  that has not is a defect, never "0". */
function amountValue(v: string): string {
  const cents = typedCents(v)
  if (cents === null) throw new Error('ruleWire: writing an amount that does not parse')
  return wireAmount(cents)
}

function writeCondition(c: ConditionDraft): ConditionWrite {
  if (c.field === 'amount') {
    // Always a direction on an amount, and only on an amount (review focus 4).
    const direction = amountDirection(c)
    if (c.op === ANY_AMOUNT) return { field: c.field, operator: c.op, value: '0', direction, case_sensitive: false }
    if (c.op === 'between') {
      return {
        field: c.field, operator: c.op, value: amountValue(c.value), second_value: amountValue(c.value2),
        direction, case_sensitive: false,
      }
    }
    return { field: c.field, operator: c.op, value: amountValue(c.value), direction, case_sensitive: false }
  }
  if (isListOp(c.op)) {
    return { field: c.field, operator: c.op, value: c.list.map((s) => s.trim()).filter(Boolean).join(','), case_sensitive: false }
  }
  if (c.op === 'between') {
    return { field: c.field, operator: c.op, value: c.value.trim(), second_value: c.value2.trim(), case_sensitive: false }
  }
  return { field: c.field, operator: c.op, value: c.value.trim(), case_sensitive: false }
}

function firstValue(d: RuleDraft): string {
  for (const g of d.groups) {
    for (const c of g.conditions) {
      if (!isCompleteCondition(c)) continue
      if (c.field === 'amount' && c.op === ANY_AMOUNT) continue
      // The canonical amount, so "$1,000.50" and "1000.5" name the rule alike.
      if (c.field === 'amount') return amountValue(c.value)
      return isListOp(c.op) ? (c.list[0] ?? '') : c.value.trim()
    }
  }
  return ''
}

export function toWrite(
  draft: RuleDraft,
): Pick<Create, 'name' | 'is_enabled' | 'conditions' | 'actions'> {
  const conditions = draft.groups
    .map((g) => ({
      logic_operator: g.join,
      conditions: g.conditions.filter(isCompleteCondition).map(writeCondition),
    }))
    .filter((g) => g.conditions.length > 0)
  const actions = draft.actions.filter(isCompleteAction).map((a) => ({
    type: a.type,
    value: ACTIONS[a.type].value === 'none' ? 'true' : a.value.trim(),
  }))
  const first = actions[0]
  const lead = firstValue(draft)
  const label = first ? ACTIONS[first.type].label : ''
  // A rule of "any amount" alone has no value to lead with: just the action.
  const fallback = lead === '' ? label : `${lead} → ${label}`
  const name = (draft.name.trim() || fallback).slice(0, NAME_MAX)
  // No `stop_processing`: the builder has no control for it, so an edit must
  // leave the stored value alone. Only a create sets it (useCreateRule).
  return { name, is_enabled: draft.enabled, conditions, actions }
}

export function isSavable(draft: RuleDraft): boolean {
  return (
    draft.groups.some((g) => g.conditions.some(isCompleteCondition)) &&
    draft.groups.every((g) => g.conditions.every((c) => conditionProblem(c) === null)) &&
    draft.actions.some(isCompleteAction)
  )
}
